import React, { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import * as echarts from "echarts";
import { formatMoney, formatRate, termLabel } from "./resultModel";
import "./CompareModal.css";

// The pricing engine page's Compare dialog (plan ppe-ui-option-b, UI-3). It
// replaces the shared MortgageCalculators/CompareProductModal on this page
// only: a dialog sized to its content, the cumulative-interest curve kept,
// and the best value in each row marked.

// One fixed colour per compared rate (up to four), so a line and its column
// always match and no colour comes out too pale to see.
export const COMPARE_COLORS = ["#325CEB", "#C6613F", "#208029", "#7A4FD6"];

const termYears = (product, row) => {
  const fromRaw = Number(row.raw && row.raw.Term);
  if (fromRaw > 0) return fromRaw;
  const m = /(\d+)/.exec(String(product.term || ""));
  return m ? Number(m[1]) : 30;
};

const loanAmountOf = (row) => Number(row.raw && row.raw.loanAmount) || 0;

// Interest paid by the end of each year, the same curve the old dialog drew.
export function yearlyInterest(rate, years, loanAmount) {
  const monthlyRate = rate / 100 / 12;
  const n = years * 12;
  if (!(loanAmount > 0) || !(n > 0)) return [];
  const payment = monthlyRate === 0
    ? loanAmount / n
    : loanAmount * monthlyRate * Math.pow(1 + monthlyRate, n) / (Math.pow(1 + monthlyRate, n) - 1);
  let balance = loanAmount;
  let paid = 0;
  const out = [];
  for (let month = 1; month <= n; month++) {
    const interest = balance * monthlyRate;
    paid += interest;
    balance -= payment - interest;
    if (month % 12 === 0) out.push(paid);
  }
  return out;
}

const shortMoney = (v) => {
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
};

// The rows of the table. `best` says which end of the row is better for the
// borrower; rows without it are facts, not something to pick on.
const FIELDS = [
  { label: "Lender", value: (p) => p.lenderName },
  { label: "Loan amount", value: (p, r) => formatMoney(loanAmountOf(r)) },
  { label: "Term", value: (p) => termLabel(p.term) },
  { label: "Rate", value: (p, r) => formatRate(r.rate), num: (p, r) => r.rate, best: "low" },
  { label: "APR", value: (p, r) => formatRate(r.apr), num: (p, r) => r.apr, best: "low" },
  { label: "PMT", value: (p, r) => formatMoney(r.pmt), num: (p, r) => Math.round(r.pmt), best: "low" },
  { label: "Cost to borrower", value: (p, r) => formatMoney(r.closing), num: (p, r) => Math.round(r.closing), best: "low" },
  { label: "Needs PMI", value: (p, r) => (Number(r.raw && r.raw.PMICost) ? "Yes" : "No") },
  { label: "Prepaid interest", value: (p, r) => formatMoney(r.raw && r.raw.prepaid_interest) },
];

// For each row with a `best`, the column indexes holding the best value; empty
// when every compared rate has the same value (nothing to point at).
export function bestColumns(field, items) {
  if (!field.best || items.length < 2) return [];
  const values = items.map(({ product, row }) => field.num(product, row));
  if (values.some((v) => !Number.isFinite(v))) return [];
  const target = field.best === "low" ? Math.min(...values) : Math.max(...values);
  if (values.every((v) => Math.abs(v - target) < 1e-9)) return [];
  return values.map((v, i) => (Math.abs(v - target) < 1e-9 ? i : -1)).filter((i) => i >= 0);
}

function InterestChart({ items }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = echarts.init(ref.current);
    const series = items.map(({ product, row }) => yearlyInterest(row.rate, termYears(product, row), loanAmountOf(row)));
    const length = Math.max(0, ...series.map((s) => s.length));
    const startYear = new Date().getFullYear();
    chart.setOption({
      animation: false,
      grid: { top: 8, bottom: 24, left: 56, right: 8 },
      tooltip: {
        trigger: "axis",
        valueFormatter: (v) => formatMoney(v),
      },
      xAxis: {
        type: "category",
        data: Array.from({ length }, (_, i) => startYear + i + 1),
        axisLabel: { color: "#6e6e70", fontSize: 12, fontFamily: "Inter", interval: 9 },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#dbdcde" } },
      },
      yAxis: {
        type: "value",
        splitNumber: 4,
        axisLabel: { color: "#6e6e70", fontSize: 12, fontFamily: "Inter", formatter: shortMoney },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: series.map((data, i) => ({
        name: `Rate ${i + 1}`,
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { color: COMPARE_COLORS[i % COMPARE_COLORS.length], width: 2 },
        itemStyle: { color: COMPARE_COLORS[i % COMPARE_COLORS.length] },
        data,
      })),
    });
    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [items]);
  return (
    <div
      ref={ref}
      className="ppe-compare-chart-canvas"
      role="img"
      aria-label="Interest paid by year for each compared rate; the table below has the numbers"
    />
  );
}

// rows: [{ product, row }] from the results; onRemove(productId) drops one,
// onClose closes the dialog.
export default function CompareModal({ rows, onRemove, onClose }) {
  const items = rows || [];
  const [showChart, setShowChart] = useState(true);
  return (
    <Modal
      open={items.length > 0}
      onCancel={onClose}
      footer={null}
      width={960}
      centered
      className="ppe-compare-modal"
      title="Compare rates"
      closable={{ "aria-label": "Close compare" }}
      destroyOnClose
    >
      <div className="ppe-compare-chart-head">
        <span className="ppe-compare-chart-title">Interest paid over time</span>
        <button type="button" className="ppe-compare-link" aria-expanded={showChart} onClick={() => setShowChart((v) => !v)}>
          {showChart ? "Hide chart" : "Show chart"}
        </button>
      </div>
      {showChart && items.length > 0 && <InterestChart items={items} />}
      <table className="ppe-compare-table">
        <colgroup>
          <col className="ppe-compare-label-col" />
          {items.map(({ product }) => <col key={product.id} />)}
        </colgroup>
        <thead>
          <tr>
            <th scope="col" className="ppe-compare-label">Product</th>
            {items.map(({ product }, i) => (
              <th scope="col" key={product.id}>
                <div className="ppe-compare-rate-tag">
                  <span className="ppe-compare-dot" style={{ background: COMPARE_COLORS[i % COMPARE_COLORS.length] }} aria-hidden="true" />
                  <span>Rate {i + 1}</span>
                  {items.length > 1 && (
                    <button
                      type="button"
                      className="ppe-compare-remove"
                      aria-label={`Remove rate ${i + 1} from compare`}
                      onClick={() => onRemove(product.id)}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg>
                    </button>
                  )}
                </div>
                <div className="ppe-compare-product">{product.name}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FIELDS.map((field) => {
            const best = bestColumns(field, items);
            return (
              <tr key={field.label}>
                <th scope="row" className="ppe-compare-label">{field.label}</th>
                {items.map(({ product, row }, i) => (
                  <td key={product.id} className={best.includes(i) ? "ppe-compare-best" : undefined}>
                    {field.value(product, row)}
                    {best.includes(i) && <span className="ppe-compare-best-tag">{field.best === "low" ? "Lowest" : "Highest"}</span>}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Modal>
  );
}
